import {useCallback} from 'react';

export const BOUNDARY_CODE = 'customSubmissionBoundary';

/**
 * Returns true if the survey's section structure has changed in a way that could
 * affect existing supplier submissions — number of sections, section order, or
 * the position of the submission boundary.
 */
export const hasStructuralChanges = (survey, existingSurvey) => {
    if (!existingSurvey || !survey?.surveyId) return false;
    const curr = survey?.SurveySections ?? [];
    const prev = existingSurvey?.SurveySections ?? [];
    const prevBoundaryIdx = prev.findIndex(s => s.code === BOUNDARY_CODE);

    // If the survey had no boundary before, no locked submissions can exist
    // (the backend rejects submit on boundary-less surveys). Safe to skip the warning.
    if (prevBoundaryIdx === -1) return false;

    if (curr.length !== prev.length) return true;
    const currOrder = curr.map(s => s.surveySectionId ?? s._tempId);
    const prevOrder = prev.map(s => s.surveySectionId ?? s._tempId);
    if (currOrder.some((id, i) => id !== prevOrder[i])) return true;
    const currBoundaryIdx = curr.findIndex(s => s.code === BOUNDARY_CODE);
    return currBoundaryIdx !== prevBoundaryIdx;
};

/**
 * Returns a memoized function that determines whether a given survey section is
 * read-only for the current user. Sections below the submission boundary are
 * read-only for non-reviewers.
 */
export const useIsSectionReadOnly = (survey, canReviewSubmissions) => {
    return useCallback((section) => {
        if (canReviewSubmissions) return false;
        const boundarySection = (survey?.SurveySections ?? []).find(s => s.code === BOUNDARY_CODE);
        if (!boundarySection) return false;
        return Number(section.order) > Number(boundarySection.order);
    }, [survey, canReviewSubmissions]);
};
